/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import {
	CosmosButton,
	CosmosRichText,
	CosmosTable,
	CosmosTagProps,
	CosmosText,
	CosmosTextVariantEnum,
	TextLink,
} from "@components/elements/atoms";
import { TagList } from "@components/elements/molecules";
import { CosmosPluginDownloadDetailFragment, PluginListItemFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { formatDate, formatFileSize } from "../blob-table/BlobTable.utils";

export interface PluginListTableItemProps extends PluginListItemFragment {
	showUpdatedDate?: boolean;
	showSize?: boolean;
	tags?: CosmosTagProps[];
	latestDownload?: CosmosPluginDownloadDetailFragment;
}

export const PluginListTableItem = ({
	title,
	desc,
	learnMoreUrl,
	latestDownload,
	extraButtons,
	showUpdatedDate = true,
	showSize = true,
	tags = [],
}: PluginListTableItemProps): React.JSX.Element | null => {
	const messages = useMessages();

	if (!title) return null;

	// Build the description line with links
	const descParts: React.ReactNode[] = [];
	if (desc) {
		descParts.push(<CosmosRichText variant="paragraphSM" color="eds.text.secondary" html={desc} />);
	}

	if (latestDownload?.tpsNoticesUrl) {
		if (descParts.length > 0) {
			descParts.push(<span key="sep1"> - </span>);
		}
		descParts.push(
			<TextLink
				key="tps"
				href={latestDownload.tpsNoticesUrl}
				label={messages["epic.cosmos.plugin.tpsNotices"] || "TPS Notices"}
				variant={CosmosTextVariantEnum.paragraphSM}
			/>,
		);
	}

	if (latestDownload?.releaseNotesUrl) {
		if (descParts.length > 0) {
			descParts.push(<span key="sep2"> - </span>);
		}
		descParts.push(
			<TextLink
				key="release"
				href={latestDownload.releaseNotesUrl}
				label={messages["epic.cosmos.plugin.releaseNotes"] || "Release Notes"}
				variant={CosmosTextVariantEnum.paragraphSM}
			/>,
		);
	}

	return (
		<CosmosTable.Row w="full">
			<CosmosTable.Cell gap="eds.dimension.8" alignSelf="center">
				<VStack gap="eds.dimension.8" alignItems="flex-start">
					<CosmosText variant={CosmosTextVariantEnum.paragraphLGBold} color="eds.text.primary" text={title} />
					{descParts.length > 0 ? (
						<div
							className={css({
								textStyle: "paragraphSM",
								color: "eds.text.secondary",
								"& a": {
									display: "inline-block",
									textDecoration: "underline",
								},
							})}
						>
							{descParts}
						</div>
					) : null}
					{tags.length > 0 ? <TagList tags={tags} size="uiSM" /> : null}
				</VStack>
			</CosmosTable.Cell>
			{showSize ? (
				<CosmosTable.Cell alignSelf="center">
					<CosmosText variant="uiMD" color="eds.text.primary" text={formatFileSize(latestDownload?.sizeInBytes || 0)} />
				</CosmosTable.Cell>
			) : null}
			{showUpdatedDate ? (
				<CosmosTable.Cell alignSelf="center">
					<CosmosText
						variant="uiMD"
						color="eds.text.primary"
						text={formatDate(latestDownload?.dateLastUpdated || "")}
					/>
				</CosmosTable.Cell>
			) : null}
			<CosmosTable.Cell
				alignItems="center"
				justifyContent="flex-end"
				alignSelf="center"
				className={css({
					display: "flex",
				})}
			>
				<HStack gap="eds.dimension.12">
					{latestDownload?.downloadUrl ? (
						<CosmosButton
							href={latestDownload.downloadUrl}
							label={messages["epic.cosmos.plugins.download"]}
							variant="secondary"
							size="lg"
							name={title}
						/>
					) : null}
					{learnMoreUrl ? (
						<CosmosButton
							href={learnMoreUrl}
							label={messages["epic.cosmos.plugins.learnMore"] || "Learn More"}
							variant="outline"
							size="lg"
							name={`${title} Learn More`}
						/>
					) : null}
					{extraButtons?.map((button, index) => (
						<CosmosButton
							key={`${button.label}-${index}`}
							icon={button.icon}
							label={button.label}
							variant={button.variant}
							href={button.href}
							iconPosition={button.iconPosition}
							name={button.name || button.label}
							size="lg"
						/>
					))}
				</HStack>
			</CosmosTable.Cell>
		</CosmosTable.Row>
	);
};
